import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import PreviewTable from "./PreviewTable";
import { API, MappingEditor, PhaseLockControl, Stat, Section, FileUpload } from "../App";

function Phase2({ session, refresh }) {
  const [busy, setBusy] = useState(false);
  const result = session.phase_results?.phase2;
  const locked = !!(session.locked_phases || {}).phase2;
  const complete = !!(result && result.ok);

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/audit/session/${session.id}/phase2`);
      if (res.data && res.data.ok === false) throw new Error(res.data.error || "Phase 2 backend error");
      toast.success("Phase 2 complete");
      refresh();
    } catch (e) {
      toast.error(`Phase 2 failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleOverride = async (phase, identifiers, overrideType = "ignored") => {
    try {
      await axios.post(`${API}/audit/session/${session.id}/override`, {
        phase,
        identifiers: Array.isArray(identifiers) ? identifiers : [identifiers],
        resolved: overrideType === "resolved",
        override_type: overrideType || "ignored"
      });
      toast.success("Marked as correct");
      refresh();
    } catch (e) {
      toast.error("Failed to mark correct");
    }
  };

  return (
    <div className="space-y-4">
      <MappingEditor session={session} role="tax" onSaved={refresh} />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          data-testid="run-phase2-btn"
          onClick={run}
          disabled={busy || locked}
          title={locked ? "Phase 2 is locked. Unlock to re-run." : ""}
        >
          {busy ? "Running..." : "Run Phase 2"}
        </Button>
        <PhaseLockControl
          session={session}
          phaseKey="phase2"
          complete={complete}
          refresh={refresh}
        />
      </div>
      {result && result.ok && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Master Bookings" value={result.total_master_bookings} />
            <Stat label="Tax Rows" value={result.total_tax_rows} />
            <Stat label="Matched" value={result.matched} />
            <Stat
              label="Real Discrepancies"
              value={result.real_discrepancy_count}
              danger={result.real_discrepancy_count > 0}
            />
            <Stat
              label="Tax Incomplete"
              value={result.tax_incomplete_count}
            />
          </div>
          <Section
            title={`Real Discrepancies (${result.real_discrepancies?.length || 0}) — tax appears complete`}
          >
            <PreviewTable session={session} rows={result.real_discrepancies} onOverride={handleOverride} phaseKey="phase2" />
          </Section>
          <Section
            title={`Tax Incompleteness (${result.tax_incomplete?.length || 0}) — Tax Report missing nights`}
          >
            <PreviewTable session={session} rows={result.tax_incomplete} onOverride={handleOverride} phaseKey="phase2" />
          </Section>
          <Section
            title={`Cancelled Refund-only (${result.cancelled_refund_only_count}) — verify against OTA payout`}
          >
            <PreviewTable session={session} rows={result.cancelled_refund_only} />
          </Section>
          <Section title={`Skipped (no tax row, valid) — ${result.skipped_no_tax_count}`}>
            <PreviewTable session={session} rows={result.skipped_no_tax} />
          </Section>
        </div>
      )}
      {result && !result.ok && (
        <p className="text-destructive text-sm">{result.error}</p>
      )}
    </div>
  );
}


export default Phase2;
